import { cn } from "cn";
import { useState } from "react";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { FilterCustomDatePicker } from "../_components/filter-custom-date-picker";
import { useFilters } from "../_hooks/use-filters";
import { LOWER_BOUND_OPERATORS } from "../operators";
import type { FilterField } from "../types";

type DateRangeValue = { start?: string; end?: string; label?: string };

/**
 * Date-range filter. Presents a date picker sized to the active operator — two
 * bounds for `range`/`between`, a single day for the comparison operators — and
 * writes the `{ start, end, label }` value the `dateRangeCodec` encodes.
 */
export const DateRangeFilter = ({ field }: { field: FilterField }) => {
  const { filters, operatorFor, modify, clear } = useFilters();
  const value = (filters as Record<string, unknown>)[field.key] as
    | DateRangeValue
    | ""
    | undefined;
  const hasValue = !!value && typeof value === "object" && !!value.label;

  const operator = operatorFor(field);
  const isSingleBound = operator !== "range" && operator !== "between";
  const bound = LOWER_BOUND_OPERATORS.includes(operator) ? "start" : "end";

  const [open, setOpen] = useState(() => !hasValue);
  const [pickerOpen, setPickerOpen] = useState(false);

  const valueLabel = hasValue
    ? (value as DateRangeValue).label
    : EMPTY_VALUE_LABEL;

  return (
    <>
      <ActiveFilterChip
        field={field}
        valueLabel={valueLabel}
        // This widget offers no presets — every value it writes comes from the
        // custom picker, so a two-bound value is always an explicit range.
        isExplicitRange={hasValue}
        open={open}
        onOpenChange={setOpen}
        onRemove={() => clear(field.key)}
      >
        <Command
          className={cn(
            /* sorted */
            "bg-bg-surface-elevate-low",
            field.popoverWidthClassName ?? "w-50",
          )}
          shouldFilter={false}
        >
          <CommandList>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  setPickerOpen(true);
                }}
                className="cursor-pointer"
              >
                {isSingleBound ? "Pick a date…" : "Custom range…"}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </ActiveFilterChip>

      <FilterCustomDatePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        mode={isSingleBound ? "single" : "range"}
        bound={bound}
        value={
          hasValue
            ? {
                start: (value as DateRangeValue).start,
                end: (value as DateRangeValue).end,
              }
            : undefined
        }
        onChange={(date) => modify({ [field.key]: date })}
      />
    </>
  );
};
